import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageCircle, Send, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import CoachMarkdown from "@/components/coach/CoachMarkdown";
import { supabase } from "@/integrations/supabase/client";

type ChatMessage = { role: "user" | "assistant"; content: string };

const SESSION_KEY = "emrkt_community_chat_session";

const getSessionId = () => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
};

const CommunityChat = ({
  communitySlug = "alabama",
  communityName = "Alabama",
  suggestions,
}: {
  communitySlug?: string;
  communityName?: string;
  suggestions?: string[];
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const prompts = suggestions ?? [
    `What is happening in ${communityName} right now?`,
    `What bills affect ${communityName}?`,
    `Who is working on projects here?`,
  ];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setLoading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/community-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token
              ? { Authorization: `Bearer ${token}` }
              : { Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` }),
          },
          body: JSON.stringify({
            community_slug: communitySlug,
            session_id: getSessionId(),
            user_message: question,
            history: messages.slice(-8),
          }),
        },
      );

      if (!resp.ok || !resp.body) {
        const payload = await resp.json().catch(() => ({}));
        if (resp.status === 429 && payload?.daily_limit_reached) setLimitReached(true);
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: "assistant",
            content: payload?.error ?? "Something went wrong. Please try again.",
          };
          return next;
        });
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const delta = JSON.parse(payload).choices?.[0]?.delta?.content;
            if (delta) {
              answer += delta;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: answer };
                return next;
              });
            }
          } catch {
            /* partial chunk */
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "Connection problem. Please try again." };
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <div className="fixed bottom-5 right-5 z-50">
        <Button
          size="lg"
          onClick={() => setOpen(true)}
          aria-label={`Ask about ${communityName}`}
          className="rounded-full shadow-lg gap-2"
        >
          <MessageCircle className="h-4 w-4" />
          Ask about {communityName}
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-50 w-full sm:w-[400px] max-w-full">
      <Card className="flex flex-col h-[70vh] sm:h-[540px] bg-background border-border shadow-2xl rounded-t-xl sm:rounded-xl overflow-hidden">
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border/60 bg-cream">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="font-display font-semibold text-sm truncate">{communityName} assistant</p>
              <p className="text-[11px] text-muted-foreground">Local answers from emrkt data</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close chat">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Ask about local projects, bills, partners, events, and history in {communityName}.
              </p>
              <div className="flex flex-col gap-2">
                {prompts.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    className="text-left text-sm rounded-md border border-border bg-cream px-3 py-2 hover:border-primary/60 transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={
                  m.role === "user"
                    ? "max-w-[85%] rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm"
                    : "max-w-[92%] rounded-lg bg-cream border border-border px-3 py-2 text-sm prose prose-sm max-w-none prose-p:my-2"
                }
              >
                {m.role === "user" ? (
                  m.content
                ) : m.content ? (
                  <CoachMarkdown>{m.content}</CoachMarkdown>
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          ))}
        </div>

        {limitReached ? (
          <div className="border-t border-border/60 p-4 bg-cream text-sm">
            <p className="text-muted-foreground mb-3">
              You&apos;ve used today&apos;s free questions. Create a free account for more.
            </p>
            <Button asChild size="sm" className="w-full">
              <Link to="/auth">Create a free account</Link>
            </Button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="border-t border-border/60 p-3 flex items-end gap-2 bg-background"
          >
            <label htmlFor="community-chat-input" className="sr-only">
              Ask a question about {communityName}
            </label>
            <Textarea
              id="community-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              placeholder={`Ask about ${communityName}…`}
              rows={1}
              className="min-h-[42px] max-h-32 resize-none text-sm"
            />
            <Button type="submit" size="icon" disabled={loading || !input.trim()} aria-label="Send message">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
};

export default CommunityChat;